import React, { useState, useEffect } from 'react';
import { MessageCircle, X, Clock } from 'lucide-react';
import { siteConfig } from '../data/mock';
import { getBusinessStatus } from '../utils/businessHours';

const WhatsAppWidget = () => {
  const [isBusinessHours, setIsBusinessHours] = useState(false);
  const [showOfflineWarning, setShowOfflineWarning] = useState(false);
  
  useEffect(() => {
    const checkBusinessHours = () => {
      setIsBusinessHours(getBusinessStatus().isOpen);
    };
    
    // Check immediately
    checkBusinessHours();
    
    // Check every minute
    const interval = setInterval(checkBusinessHours, 60000);
    
    return () => clearInterval(interval);
  }, []);
  
  const phoneNumber = siteConfig.contact.whatsapp.replace(/\s/g, '');
  const message = encodeURIComponent('Bonjour, je souhaiterais obtenir des informations sur vos services de jardinage.');
  const whatsappUrl = `https://wa.me/${phoneNumber}?text=${message}`;

  const handleClick = (e) => {
    if (!isBusinessHours) {
      e.preventDefault();
      setShowOfflineWarning(true);
    }
  };

  return (
    <>
      {/* Offline Warning Modal */}
      {showOfflineWarning && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 relative animate-in fade-in zoom-in duration-200">
            <button 
              onClick={() => setShowOfflineWarning(false)}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X size={20} />
            </button>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-amber-600" />
              </div>
              
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Je suis actuellement hors ligne
              </h3>
              
              <p className="text-gray-600 mb-4">
                Mes horaires de disponibilité WhatsApp :
              </p>
              
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-4">
                <p className="font-semibold text-emerald-800">
                  Lundi, Mardi, Jeudi, Vendredi
                </p>
                <p className="text-emerald-700 text-lg font-bold">
                  11h00 — 17h00
                </p>
                <p className="text-sm text-emerald-600 mt-1">
                  (heure de Paris) · Fermé mer, sam, dim
                </p>
              </div>
              
              <p className="text-sm text-gray-500 mb-4">
                En dehors de ces horaires, vous pouvez me contacter via le formulaire de devis. Je vous répondrai dès que possible !
              </p>
              
              <div className="flex flex-col gap-2">
                <a 
                  href="/devis"
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3 px-4 rounded-xl transition-colors"
                >
                  Demander un devis express
                </a>
                <button 
                  onClick={() => setShowOfflineWarning(false)}
                  className="w-full text-gray-500 hover:text-gray-700 font-medium py-2 transition-colors"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* WhatsApp Button */}
      <button
        onClick={handleClick}
        className={`fixed bottom-24 right-6 lg:bottom-6 z-50 text-white rounded-full p-4 shadow-2xl transition-all duration-300 group ${
          isBusinessHours 
            ? 'bg-green-500 hover:bg-green-600 hover:scale-110 cursor-pointer' 
            : 'bg-gray-400 hover:bg-gray-500 cursor-pointer'
        }`}
        aria-label="Contactez-nous sur WhatsApp"
        data-testid="whatsapp-widget"
      >
        {/* When online, wrap in link */}
        {isBusinessHours ? (
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="block"
          >
            <MessageCircle size={32} />
          </a>
        ) : (
          <MessageCircle size={32} />
        )}
        
        {/* Tooltip */}
        <div className="absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-gray-900 text-white px-4 py-2 rounded-lg text-sm whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
          <div className="font-semibold">
            {isBusinessHours ? 'Besoin d\'aide ?' : 'Hors ligne'}
          </div>
          <div className="text-xs">
            {isBusinessHours ? 'En ligne - Réponse rapide' : 'Cliquez pour voir les horaires'}
          </div>
          {/* Arrow */}
          <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-full">
            <div className="border-8 border-transparent border-l-gray-900"></div>
          </div>
        </div>

        {/* Online/Offline indicator with animation */}
        {isBusinessHours ? (
          <>
            {/* Green pulsing indicator when online */}
            <div className="absolute top-0 right-0 w-4 h-4 bg-green-400 border-2 border-white rounded-full animate-ping"></div>
            <div className="absolute top-0 right-0 w-4 h-4 bg-green-400 border-2 border-white rounded-full"></div>
          </>
        ) : (
          <>
            {/* Red indicator when offline (no pulse) */}
            <div className="absolute top-0 right-0 w-4 h-4 bg-red-500 border-2 border-white rounded-full"></div>
          </>
        )}
      </button>
    </>
  );
};

export default WhatsAppWidget;
